<template>

    <div v-if="this.siteOptions" class="card card-default">
        <div class="card-header flex-column align-items-stretch">
            <h1>Reports</h1>

            <div v-if="(Object.keys(this.companyOptions).length > 1)" class="row control-panel">
                <div class="col">
                    <div class="form-group form-control-lg">
                        <label for="company-select">Company:</label>

                        <select2
                            id="company-select"
                            class="form-control site-select"
                            v-model="companySelected"
                            :options="select2CompanyOptions"
                            :settings="{width: '100%'}"
                            @change="changeCompany"
                            @select="changeCompany"
                        />
                    </div>
                </div>
            </div>

            <div class="row control-panel justify-content-between">
                <div class="col-md control-panel-item">
                    <div class="form-group form-control-lg">
                        <label for="site-select">Site:</label>

                        <select2
                            id="site-select"
                            class="form-control site-select"
                            v-model="siteSelected"
                            :options="select2SiteOptions"
                            :settings="{width: '100%'}"
                            @change="getSiteData(1, 'all', false, sort.by, sort.direction)"
                            @select="getSiteData(1, 'all', false, sort.by, sort.direction)"
                        />
                    </div>
                </div>

                <div class="col-md-3 control-panel-item">
                    <div class="form-group form-control-lg">
                        <label for="range-selector">Filter:</label>

                        <input id="date-range-picker" type="text" class="form-control">
                    </div>
                </div>

                <div class="col-md-auto control-panel-item data-view-controls">
                    <button
                        :disabled="!siteHasTableData"
                        class="btn btn-secondary"
                        @click="showViewSettingsModal"
                    >
                        <i class="fa fa-cog"></i><span>View Settings</span>
                    </button>

                    <button
                        :disabled="!siteHasTableData"
                        class="btn btn-secondary"
                        @click="showReportSettingsModal"
                    >
                        <i class="fa fa-download"></i><span>Download Report...</span>
                    </button>
                </div>
            </div>

        </div>

        <div class="card-body site-data-panel">
            <div :class="['loadingSpinner', {'d-none' : !loadingState}]">
                <i class="fas fa-sync fa-3x fa-spin"></i>
            </div>

            <div v-if="siteHasTableData"
                 :class="['data-container', {'panel-loading' : loadingState}]">

                <site-charts-component :site-data="this.siteData"></site-charts-component>

                <div class="row">
                    <div class="col-12">
                        <hr>
                    </div>
                </div>

                <div id="site-data-table" class="row">

                    <div class="col-12">


                        <div class="row justify-content-between align-items-center mb-3">

                            <div class="col-auto">

                                <ul id="data-view-switcher" class="nav nav-pills">
                                    <li class="nav-item">
                                        <button
                                            :aria-selected="(viewMode === 'overview' ? 'true' : 'false')"
                                            :class="['nav-link', { 'active': viewMode === 'overview' }]"
                                            v-on:click="switchView('overview')"
                                        >
                                            Overview
                                        </button>
                                    </li>
                                    <li class="nav-item">
                                        <button
                                            :aria-selected="(viewMode === 'details' ? 'true' : 'false')"
                                            :class="['nav-link', { 'active': viewMode === 'details' }]"
                                            v-on:click="switchView('details')"
                                        >
                                            Details
                                        </button>
                                    </li>
                                    <li class="nav-item">
                                        <button
                                            :aria-selected="(viewMode === 'project' ? 'true' : 'false')"
                                            :class="['nav-link', { 'active': viewMode === 'project' }]"
                                            v-on:click="switchView('project')"
                                        >
                                            Project
                                        </button>
                                    </li>
                                    <li class="nav-item">
                                        <button
                                            :aria-selected="(viewMode === 'materials' ? 'true' : 'false')"
                                            :class="['nav-link', { 'active': viewMode === 'materials' }]"
                                            v-on:click="switchView('materials')"
                                        >
                                            Material Types
                                        </button>
                                    </li>
                                    <li class="nav-item">
                                        <button
                                            :aria-selected="(viewMode === 'carbon' ? 'true' : 'false')"
                                            :class="['nav-link', { 'active': viewMode === 'carbon' }]"
                                            v-on:click="switchView('carbon')"
                                        >
                                            Carbon Reporting
                                        </button>
                                    </li>
                                </ul>

                            </div>

                            <div class="col-auto d-flex align-items-center" v-if="hiddenFieldsCount > 0">

                                <p class="text-muted m-0">
                                    {{ hiddenFieldsCount === 1 ? '1 field hidden' : `${hiddenFieldsCount} fields hidden` }}
                                </p>

                                <button type="button" class="btn btn-sm btn-link ml-2" @click="showViewSettingsModal">Change</button>

                            </div>

                        </div>


                        <div class="row">

                            <div class="col-12">

                                <site-table-component
                                    :currentSort="this.sort.by"
                                    :currentSortDir="this.sort.direction"
                                    :rawData="this.siteData"
                                    :view-mode="this.viewMode"
                                    :view-units="viewUnits"
                                    :visible-fields="visibleFields"
                                    v-on:update:sort="updateSortProps($event)"
                                    v-on:show:files="showFileModal($event)"
                                    v-on:page:set="changePage($event)"
                                ></site-table-component>

                            </div>

                        </div>


                    </div>

                </div>

            </div>

            <div v-else-if="!loadingState" id="no-data-alert" class="alert alert-warning" role="alert">
                <i class="fa fa-exclamation-triangle"></i> No data for the current selection.
            </div>

            <SiteFileModalComponent
                :files="fileModal.files"
                :is-open="fileModal.isOpen"
                :line-data="fileModal.currentLine"
                v-on:close:modal="hideFileModal"
            ></SiteFileModalComponent>

        </div>

        <div id="report-generate-modal" class="modal" tabindex="-1" data-backdrop="static" data-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-body">
                        <p>
                            <i class="fas fa-sync fa-2x fa-spin"></i>
                            <i class="fas fa-check fa-2x"></i>
                            <span class="text"></span>
                        </p>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-light" type="button" id="report-generate-cancel">Cancel</button>
                        <button class="btn btn-light" data-dismiss="modal" type="button" id="report-generate-done">Done</button>
                        <a href="#" target="_blank" class="btn btn-primary" id="report-generate-download">Download Report</a>
                    </div>
                </div>
            </div>
        </div>

        <div id="error-modal" class="modal" tabindex="-1" data-backdrop="static" data-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-body">
                        <p>
                            There was a problem generating the report. Please contact your account manager or Ward
                            directly for further assistance.
                        </p>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-primary" data-dismiss="modal" type="button">OK</button>
                    </div>
                </div>
            </div>
        </div>

        <div id="report-settings-modal" class="modal" tabindex="-1" data-backdrop="static">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Download Report</h5>
                        <button aria-label="Close" class="close" data-dismiss="modal" type="button">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div class="modal-body">

                        <form>

                            <fieldset class="form-group row">

                                <legend class="col-form-label col-sm-4 float-sm-left pt-0">Type of Report</legend>

                                <div class="col-sm-8">

                                    <div class="form-check">
                                        <input id="report-download-type-standard" class="form-check-input" name="report-download-type"
                                               type="radio"
                                               value="standard"
                                               v-model="requestedReportType"
                                               v-bind:value="'standard'"
                                        >
                                        <label class="form-check-label" for="report-download-type-standard">Standard Report</label>
                                        <small class="form-text text-muted mt-0 pb-2">Includes fields from all tabs of the data table.</small>
                                    </div>

                                    <div class="form-check">
                                        <input id="report-download-type-projects" class="form-check-input" name="report-download-type"
                                               type="radio"
                                               value="projects"
                                               v-model="requestedReportType"
                                               v-bind:value="'projects'"
                                        >
                                        <label class="form-check-label" for="report-download-type-projects">Projects Report</label>
                                        <small class="form-text text-muted mt-0 pb-2">Lines listed by project with plastic/metal/glass calculations.</small>
                                    </div>

                                </div>

                            </fieldset>

                            <fieldset class="form-group row">

                                <legend class="col-form-label col-sm-4 float-sm-left pt-0">File Format</legend>

                                <div class="col-sm-8">

                                    <div class="form-check">
                                        <input id="report-download-format-csv" class="form-check-input" name="report-download-format"
                                               type="radio"
                                               value="csv"
                                               v-model="requestedReportFormat"
                                               v-bind:value="'csv'"
                                        >
                                        <label class="form-check-label" for="report-download-format-csv">Comma-Separated Values (.csv)</label>
                                    </div>

                                    <div class="form-check">
                                        <input id="report-download-format-excel" class="form-check-input" name="report-download-format"
                                               type="radio"
                                               value="excel"
                                               v-model="requestedReportFormat"
                                               v-bind:value="'excel'"
                                        >
                                        <label class="form-check-label" for="report-download-format-excel">Microsoft Excel Workbook (.xlsx)</label>
                                    </div>

                                </div>

                            </fieldset>

                            <div class="form-group row">

                                <p class="col-form-label col-sm-4 float-sm-left pt-0">Units to Show</p>

                                <div class="col-sm-8">
                                    {{ viewUnitsLabel }}
                                    <button type="button" class="btn btn-sm btn-link ml-1" @click="showViewSettingsModal">Change</button>
                                </div>

                            </div>

                            <div class="form-group row">

                                <p class="col-form-label col-sm-4 float-sm-left pt-0">Visible Fields</p>

                                <div class="col-sm-8">
                                    {{ hiddenFieldsCount === 0 ? 'All' : `${hiddenFieldsCount} hidden` }}
                                    <button type="button" class="btn btn-sm btn-link ml-1" @click="showViewSettingsModal">Change</button>
                                </div>

                            </div>

                        </form>

                    </div>

                    <div class="modal-footer">
                        <button class="btn btn-secondary" data-dismiss="modal" type="button">Cancel</button>
                        <button class="btn btn-primary" type="button" @click="downloadReport">Download</button>
                    </div>
                </div>
            </div>
        </div>

        <div id="view-settings-modal" class="modal" tabindex="-1" data-backdrop="static">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">View Settings</h5>
                        <button aria-label="Close" class="close" data-dismiss="modal" type="button">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div class="modal-body">

                        <div class="alert alert-secondary" role="alert">
                            <i class="fa fa-info-circle"></i> Settings selected here apply to both the tables you see
                            in the browser, and to downloaded reports.
                        </div>

                        <form>

                            <fieldset class="form-group row">

                                <legend class="col-form-label col-sm-4 float-sm-left pt-0">Units to Show</legend>

                                <div class="col-sm-8">

                                    <div class="form-check">
                                        <input id="view-units-percentage" class="form-check-input" name="view-units"
                                               type="radio"
                                               value="percentage"
                                               v-model="viewUnitsChoice"
                                               v-bind:value="'percentage'"
                                        >
                                        <label class="form-check-label" for="view-units-percentage">Percentages</label>
                                    </div>

                                    <div class="form-check">
                                        <input id="view-units-amount" class="form-check-input" name="view-units"
                                               type="radio"
                                               value="amount"
                                               v-model="viewUnitsChoice"
                                               v-bind:value="'amount'"
                                        >
                                        <label class="form-check-label" for="view-units-amount">Weight In Tonnes</label>
                                    </div>

                                    <div class="form-check">
                                        <input id="view-units-all" class="form-check-input" name="view-units"
                                               type="radio"
                                               value="all"
                                               v-model="viewUnitsChoice"
                                               v-bind:value="'all'"
                                        >
                                        <label class="form-check-label" for="view-units-all">Both Percentages and Weights</label>
                                    </div>

                                    <small class="form-text text-muted">
                                        Applies to fields showing amounts recycled, RDF, landfill, and material types.
                                    </small>

                                </div>

                            </fieldset>

                            <fieldset class="form-group row">

                                <legend class="col-form-label col-sm-4 float-sm-left pt-0">Visible Fields</legend>

                                <div class="col-sm-8 visible-fields-list border rounded px-2 py-1">

                                    <div v-for="(visible, field) in visibleFields"
                                         :key="field"
                                         class="form-check"
                                    >
                                        <input :id="`view-field-${field}`" :name="`view-field-${field}`"
                                               :value="field"
                                               class="form-check-input"
                                               type="checkbox"
                                               v-model="visibleFields[field]"
                                        >
                                        <label :for="`view-field-${field}`" class="form-check-label">{{
                                                getFieldDefinition(field).label
                                            }}</label>
                                    </div>

                                </div>

                            </fieldset>

                        </form>

                    </div>

                    <div class="modal-footer">
                        <button class="btn btn-primary" data-dismiss="modal" type="button">Done</button>
                    </div>
                </div>
            </div>
        </div>

    </div>

    <div v-else class="card card-default">
        <div class="card-header">
            <h1>Sites</h1>
        </div>
        <div class="card-body">
            <p>No sites to display. Please contact your administrator.</p>
        </div>
    </div>

</template>

<script>
import axios from 'axios'
import SiteTableComponent from './SiteTableComponent'
import SiteChartsComponent from './SiteChartsComponent'
import SiteFileModalComponent from './SiteFileModalComponent'
import { useDataViewTableFields } from '../../helpers/useDataViewTableFields'
import { easepick, RangePlugin, PresetPlugin, LockPlugin, KbdPlugin, AmpPlugin, DateTime } from '@easepick/bundle'
import Select2 from 'v-select2-component'

const { getField } = useDataViewTableFields()

export default {
    components: { SiteFileModalComponent, SiteChartsComponent, SiteTableComponent, Select2 },

    props: {
        viewUnits: {
            type: Object
        },
        visibleFields: {
            type: Object
        }
    },

    data: function () {
        return {
            siteData: {
                data: {
                    'table': {},
                    'graph': {}
                },
                name: 'Sites'
            },
            fileModal: {
                currentLine: {},
                files: [],
                isOpen: false
            },
            siteOptions: {},
            siteSelected: 0,
            companyOptions: {},
            companySelected: false,
            companySelectedName: 'Sites',
            periodOptions: [],
            periodSelected: this.getPeriod(),
            sort: {
                by: 'order_date',
                direction: 'desc',
            },
            viewMode: 'overview',
            loadingState: false,
            dateRangePicker: null,
            requestedReportType: 'standard',
            requestedReportFormat: 'csv'
        }
    },

    computed: {

        viewUnitsLabel: function() {
            if (this.viewUnits.percentage && this.viewUnits.amount) {
                return 'Both Percentages and Weights'
            }

            else if (this.viewUnits.amount) {
                return 'Weight In Tonnes'
            }

            return 'Percentages'
        },

        select2SiteOptions: function() {
            const hasOptions = this.siteOptions.length > 0

            if (!hasOptions) {
                return []
            }

            return this.siteOptions.map((site) => {
                return {
                    id: site.id,
                    text: site.name,
                }
            })
        },

        select2CompanyOptions: function() {
            const hasOptions = this.companyOptions.length > 0

            if (!hasOptions) {
                return []
            }

            return this.companyOptions.map((company) => {
                return {
                    id: company.id,
                    text: company.name,
                }
            })
        },

        viewUnitsChoice: {

            get: function () {
                if (this.viewUnits.percentage && this.viewUnits.amount) {
                    return 'all'
                }

                else if (this.viewUnits.amount) {
                    return 'amount'
                }

                return 'percentage'
            },

            set: function (newValue) {
                switch (newValue) {
                    case 'all' :
                        this.viewUnits.percentage = true
                        this.viewUnits.amount = true
                        break

                    case 'amount' :
                        this.viewUnits.percentage = false
                        this.viewUnits.amount = true
                        break

                    default:
                        this.viewUnits.percentage = true
                        this.viewUnits.amount = false
                }
            }

        },

        siteHasTableData: function() {
            return (Object.keys(this.siteData.data.table).length > 0)
        },

        hiddenFieldsCount: function() {
            return Object.values(this.visibleFields).filter((visible) => visible === false).length
        },

        selectedDateRange: function() {
            const startDate = this.dateRangePicker.getStartDate().clone()
            const endDate = this.dateRangePicker.getEndDate().clone()

            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return {
                from: startDate.getTime(),
                to: endDate.getTime()
            }
        }

    },

    mounted () {
        this.loadingState = true

        this.getCompanyList()
            .then(response => (this.companyOptions = response.data))
            .then(() => (this.companySelected = this.companyOptions[0].id))
            .then(() => (this.companySelectedName = this.companyOptions[0].name))
            .then(() => (
                this.getSiteList()
                    .then(() => {
                        if (this.siteOptions[0]) {
                            this.siteSelected = this.siteOptions[0].id
                            this.siteData.name = this.siteOptions[0].name
                            this.getDateOptions()
                            this.getSiteData(1, 'all', false, this.sort.by, this.sort.direction)
                        }
                        else {
                            this.siteOptions = false
                        }
                    })
            ))

        this.loadingState = false


        $('#view-settings-modal').on('hide.bs.modal', this, function(e) {
            axios.post(
                '/user/store-view-preferences',
                {
                    preferences: {
                        viewUnits: e.data.viewUnits,
                        visibleFields: e.data.visibleFields
                    }
                }
            )
        })


        const dateTime = new DateTime()

        const thisMonth = () => {
            const d1 = dateTime.clone();
            d1.setDate(1);

            const d2 = dateTime.clone();

            return [new DateTime(d1), new DateTime(d2)];
        };

        const selectedSort = this.sort
        const getSiteData = this.getSiteData

        this.dateRangePicker = new easepick.create({
            element: document.getElementById('date-range-picker'),
            css: [
                window.location.origin + '/css/easepick.css'
            ],
            plugins: [RangePlugin, PresetPlugin, LockPlugin, KbdPlugin, AmpPlugin],
            lang: 'en-GB',
            format: 'DD/MM/YYYY',
            RangePlugin: {
                startDate: thisMonth()[0],
                endDate: thisMonth()[1]
            },
            LockPlugin: {
                maxDate: dateTime,
                maxDays: 366
            },
            AmpPlugin: {
                dropdown: {
                    minYear: 2016,
                    maxYear: null,
                    months: true,
                    years: true
                }
            },
            PresetPlugin: {
                customPreset: {
                    'This Month': thisMonth(),
                    'Last 30 Days': [dateTime.clone().subtract(29, 'day'), dateTime.clone()],
                    '3 Months': [dateTime.clone().subtract(3, 'month'), dateTime.clone()],
                    '6 Months': [dateTime.clone().subtract(6, 'month'), dateTime.clone()],
                    '12 Months': [dateTime.clone().subtract(12, 'month'), dateTime.clone()],
                },
                customLabels: [
                    'This Month',
                    'Last 30 Days',
                    '3 Months',
                    '6 Months',
                    '12 Months',
                ]
            },

            setup(picker) {
                picker.on('select', (e) => {
                    getSiteData(1, 'all', false, selectedSort.by, selectedSort.direction)
                })
            }
        })
    },

    methods: {
        getFieldDefinition (name) {
            return getField(name)
        },

        getCompanyList () {
            return axios.get('/company/list')
        },

        getSiteList () {
            this.loadingState = true
            return axios({
                method: 'post',
                url: '/site/list',
                data: {
                    company: this.companySelected,
                }
            })
                .then(response => (this.siteOptions = response.data))
                .then(() => this.loadingState = false)
                .catch(function (error) {
                    console.log(error)
                })
        },

        getDateOptions () {
            axios.get('/date/list')
                .then(response => (this.periodOptions = response.data))
        },

        getSiteData (page, type, scrollToTable, sortBy, sortDirection) {
            this.loadingState = true

            if(scrollToTable) {
                document.querySelector('#site-data-table').scrollIntoView()
            }


            axios({
                method: 'post',
                url: '/site',
                data: {
                    site: this.siteSelected,
                    from: (this.selectedDateRange.from / 1000),
                    to: (this.selectedDateRange.to / 1000),
                    page,
                    type,
                    sortBy,
                    sortDirection
                }
            })
                .then(response => {
                    // Merge new data with existing data, avoids replacing the whole data set when things like
                    // graphs haven't changed.
                    // Has to be done like this because Object.assign() won't work, as Vue wraps siteData in
                    // its own Observer object.
                    for (const responseKey in response.data.data) {
                        this.siteData.data[responseKey] = response.data.data[responseKey]
                    }
                })
                .then(() => { this.loadingState = false })
                .catch(function (error) {
                    console.log(error)
                })

        },

        getPeriod () {
            let now = new Date()
            let year = now.getFullYear()
            let month = ('0' + (now.getMonth() + 1)).slice(-2)
            return year + '-' + month
        },

        downloadReport () {
            var controller = this
            var requestController = new AbortController()
            var reportSettingsModal = $('#report-settings-modal')
            var reportGenerateModal = $('#report-generate-modal')
            var reportGenerateModalContent = reportGenerateModal.find('.modal-body > p')
            var reportGenerateCancel = reportGenerateModal.find('#report-generate-cancel')
            var reportGenerateDownload = reportGenerateModal.find('#report-generate-download')
            var reportGenerateDone = reportGenerateModal.find('#report-generate-done')

            reportSettingsModal.modal('hide')

            reportGenerateModalContent.children('.text')
                .text('Generating report, this may take a while...')

            reportGenerateModalContent
                .removeClass('report-generate-success')
                .addClass('report-generate-processing')
                .show();

            reportGenerateDownload.hide()
            reportGenerateDone.hide()
            reportGenerateCancel.on('click', function(e) {
                e.preventDefault()
                requestController.abort()
                reportGenerateModal.modal('hide')
            })
                .show()

            reportGenerateModal.modal('show')

            axios({
                method: 'post',
                url: `/site/report/${this.requestedReportFormat}`,
                signal: requestController.signal,
                data: {
                    site: this.siteSelected,
                    from: (this.selectedDateRange.from / 1000),
                    to: (this.selectedDateRange.to / 1000),
                    sortBy: this.sort.by,
                    sortDirection: this.sort.direction,
                    type: this.requestedReportType,
                    viewUnits: this.viewUnits,
                    visibleFields: this.visibleFields
                }
            })
                .then(response => {
                    if (response.data.status === 'successful') {
                        reportGenerateModalContent.children('.text')
                            .text('Report ready. Click \'Download Report\' to download it.')

                        reportGenerateModalContent
                            .removeClass('report-generate-processing')
                            .addClass('report-generate-success')

                        reportGenerateCancel.hide()

                        reportGenerateDownload.attr('href', response.data.reportUrl)
                            .show()

                        reportGenerateDone.show()
                    }

                    else {
                        controller.showReportError()
                    }
                })
                .catch(function (error) {
                    console.error(error)

                    if(!axios.isCancel(error)) {
                        reportGenerateModal.modal('hide')
                        controller.showReportError()
                    }
                })
        },

        showReportError () {
            $('#error-modal').modal('show')
        },

        updateSortProps (payload) {
            this.sort.by = payload.by
            this.sort.direction = payload.direction

            this.getSiteData(1, 'table', true, this.sort.by, this.sort.direction)
        },

        changeCompany () {
            this.getSiteList()
                .then(() => (this.siteSelected = this.siteOptions[0].id))
                .then(() => this.getSiteData(1, 'all', false, this.sort.by, this.sort.direction))
        },

        switchView (type) {
            this.viewMode = type
        },

        showFileModal (payload) {
            this.fileModal.currentLine = payload
            this.fileModal.isOpen = true
            this.fileModal.files = this.siteData.data.files[payload.document_number]
        },

        hideFileModal () {
            this.fileModal.isOpen = false
        },

        showViewSettingsModal () {
            $('#view-settings-modal').modal('show')
        },

        showReportSettingsModal () {
            $('#report-settings-modal').modal('show')
        },

        changePage (page) {
            this.getSiteData(page, 'table', true, this.sort.by, this.sort.direction)
        }
    }
}
</script>

<style lang="scss">
.form-control.site-select {
    .select2-selection {
        border: unset;

        &:focus-visible {
            outline: none;
        }
    }
}

span.select2-dropdown.select2-dropdown--below {
    margin-top: 5px;
}
</style>
